import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import { strictFetcher } from './strict-fetcher'

/// One token's fold from `GET /alkane/address/:addr/ledger` — the address's
/// FULL alkane history, walked and folded server-side.
///
/// The server keeps two kinds of "what it cost" apart and so does this UI:
///
///   * exact sats — `invested_sats_exact` / `proceeds_sats_exact` /
///     `realized_sats` count only flows where the tx itself moved frBTC,
///     whose base unit IS a sat. These are read off chain, not estimated.
///   * market valuation — `valued_usd_acquired` prices mints/transfers at
///     the receipt date's daily close from our own candle series, and
///     `qty_valued_usd`/`qty_unvalued` say exactly which quantity that
///     valuation covers. Never added to the exact side.
export interface LedgerToken {
	id: string
	/// Net fold quantity (base units, signed decimal string).
	net_qty: string
	/// Live balance from the balance book, or null when it was unreachable.
	balance: string | null
	/// Fold net vs live balance. `false` is a caveat the UI must show, `null`
	/// means the balance book was unreachable — no claim either way.
	reconciled: boolean | null
	coverage: 'full' | 'truncated'
	invested_sats_exact: string
	qty_bought_exact: string
	qty_bought_unpriced: string
	qty_minted: string
	qty_transferred_in: string
	qty_lp_out: string
	qty_sold: string
	qty_transferred_out: string
	qty_lp_in: string
	proceeds_sats_exact: string
	/// Average-cost realized PnL in sats (signed decimal string).
	realized_sats: string
	/// Exact-sats basis still attached to the remaining position.
	remaining_basis_sats: string
	/// Sats per WHOLE token over the exactly-priced buys only.
	avg_entry_sats_per_token: number | null
	valued_usd_acquired: number | null
	qty_valued_usd: string
	qty_unvalued: string
	first_ts: number | null
	last_ts: number | null
}

interface LedgerBody {
	address: string
	/// The walk hit its page cap or some txs could not be decoded yet — every
	/// figure is a floor over what WAS walked, and the UI says so.
	truncated: boolean
	txs_indexed: number
	tokens: LedgerToken[]
	updated_at: number
}

/// The address's full-history ledger, keyed by token id.
///
/// `unavailable` is the deployed-API-predates-the-endpoint / upstream-down
/// case (a thrown error with no data) — the portfolio then falls back to the
/// old windowed cost-basis path rather than showing nothing. strictFetcher,
/// not the lenient one: only a thrown error may reach `unavailable`.
export default function useLedger(address: string | null): {
	tokens: Map<string, LedgerToken> | undefined
	truncated: boolean
	unavailable: boolean
	isLoading: boolean
} {
	const { data, error, isLoading } = useSwr<LedgerBody>(
		address ? `${API_URL}/alkane/address/${address}/ledger` : null,
		strictFetcher,
		{ refreshInterval: 120_000, revalidateOnFocus: false }
	)
	const tokens = Array.isArray(data?.tokens)
		? new Map(data!.tokens.map((t) => [t.id, t]))
		: undefined
	return {
		tokens,
		truncated: data?.truncated ?? false,
		unavailable: !!error && !data,
		isLoading
	}
}
